// The review / edit view's header: which sheet (Crashing or Box-outs), which event, its date and whose numbers it
// holds. The event picker sets a sheet to Practice (its date and label editable here) or to one of POR's games (the
// game's own date); the page decides when it is locked (a game's own crashing sheet stays on its game).
import React from "react";
import { Palette } from "./reboundingModel";
import { BoardGame, ReboundingPage, SheetSession, UploadOrigin } from "./types";
import { md, label11 as label11Of, pill, RED } from "./ui";
import { eventText, gameOption, isGame, ORIGIN_TEXT } from "./uploadLabels";

interface Props {
  P: Palette;
  field: React.CSSProperties;
  sheet: ReboundingPage;
  // the event the sheet is set to now
  session: SheetSession;
  origin: UploadOrigin;
  date: string;
  setDate: (d: string) => void;
  label: string;
  setLabel: (l: string) => void;
  games?: BoardGame[];
  // the picker: a game's session id, or null for Practice; absent = the event is fixed
  onEvent?: (gameId: string | null) => void;
}

const SheetHeader: React.FC<Props> = ({
  P,
  field,
  sheet,
  session,
  origin,
  date,
  setDate,
  label,
  setLabel,
  games = [],
  onEvent,
}) => {
  const label11 = label11Of(P);
  const game = isGame(session);
  const chip = (text: string, style: React.CSSProperties, testId: string) => (
    <div
      data-testid={testId}
      style={{
        height: 38,
        display: "flex",
        alignItems: "center",
        padding: "0 12px",
        fontFamily: "var(--font-display)",
        fontWeight: 900,
        fontSize: 24,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        ...style,
      }}
    >
      {text}
    </div>
  );
  // Switching to Game picks the game on the sheet's date, else the latest.
  const defaultGame = (games.find((g) => g.date === date) ?? games[0])
    ?.sessionId;
  const picker = onEvent && (
    <div
      data-testid="event-picker"
      style={{ display: "flex", alignItems: "center", gap: 8 }}
    >
      <div style={{ display: "flex" }}>
        <button
          onClick={() => onEvent(null)}
          aria-pressed={!game}
          style={{ ...pill(P, !game, 16), height: 38 }}
        >
          PRACTICE
        </button>
        <button
          onClick={() => !game && defaultGame && onEvent(defaultGame)}
          aria-pressed={game}
          disabled={!defaultGame}
          title={defaultGame ? undefined : "No Portland games yet this season"}
          style={{
            ...pill(P, game, 16),
            height: 38,
            opacity: defaultGame ? 1 : 0.35,
          }}
        >
          GAME
        </button>
      </div>
      {game && (
        <select
          value={session.sessionId ?? ""}
          onChange={(e) => onEvent(e.target.value)}
          aria-label="Game"
          style={{
            ...field,
            background: RED,
            color: "#fff",
            borderColor: RED,
            cursor: "pointer",
          }}
        >
          {games.map((g) => (
            <option
              key={g.sessionId}
              value={g.sessionId}
              style={{ background: P.bg, color: P.fg }}
            >
              {gameOption(g)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${game ? 4 : 5}, auto)`,
        columnGap: 18,
        rowGap: 2,
        alignItems: "center",
      }}
    >
      <span style={label11}>SHEET</span>
      <span style={label11}>EVENT</span>
      <span style={label11}>{game ? "GAME DATE" : "PRACTICE DATE"}</span>
      {!game && <span style={label11}>LABEL</span>}
      <span style={label11}>SOURCE</span>
      {chip(
        sheet === "crashing" ? "CRASHING" : "BOX-OUTS",
        { background: P.fg, color: P.bg },
        "sheet-chip",
      )}
      {picker ||
        chip(
          eventText(session),
          game
            ? { background: RED, color: "#fff" }
            : { border: `2px solid ${P.fg}` },
          "event-chip",
        )}
      {game ? (
        chip(
          session.date ? md(session.date) : "",
          { border: `1px solid ${P.pillBorder}`, fontWeight: 800 },
          "date-chip",
        )
      ) : (
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Practice date"
          style={{
            ...field,
            borderColor: date ? P.pillBorder : P.bad,
            width: 190,
          }}
        />
      )}
      {!game && (
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Training Camp"
          aria-label="Label"
          style={{
            ...field,
            fontFamily: "var(--font-body)",
            fontWeight: 700,
            fontSize: 17,
            width: 260,
          }}
        />
      )}
      {chip(
        ORIGIN_TEXT[origin],
        {
          border: `1px solid ${P.pillBorder}`,
          fontSize: 16,
          letterSpacing: ".12em",
          color: P.sub,
        },
        "origin-chip",
      )}
    </div>
  );
};

export default SheetHeader;
